import { ConfigProvider } from 'antd';
import { Amplify, Hub } from 'aws-amplify';
import { useAuth } from 'hooks/useAuth';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_TRANSLATION_LANGUAGE,
  MAPPING_ANTD_LOCALE_BY_LANGUAGE
} from 'util/translation/constant';
import defaultErrorMessagesJson from 'util/error-message';
import { setUserPolicyAgreement } from 'util/auth';
import Routes from './routes';
import awsConfig from './util/config/aws-exports';

Amplify.configure(awsConfig);

function App() {
  const [isCheckingAuth, isLoggedIn] = useAuth();
  const { t, i18n } = useTranslation();
  const errorMessagesJson = t('errorCodes', { returnObjects: true });

  useEffect(() => {
    if (typeof errorMessagesJson === 'object') {
      // The function errorHandler is out of scope in React, so we must put global error messages to window scope
      // From there, we can get the error messages and show them on the UI
      window.globalErrorMessagesJson = errorMessagesJson;
    } else {
      window.globalErrorMessagesJson = defaultErrorMessagesJson;
    }
  }, [errorMessagesJson]);

  useEffect(() => {
    if (!i18n.language) {
      i18n.changeLanguage(DEFAULT_TRANSLATION_LANGUAGE);
    }
  }, [i18n]);

  useEffect(() => {
    Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
          setUserPolicyAgreement();
          break;
        default:
          break;
      }
    });
  }, []);

  const locale = useMemo(
    () => MAPPING_ANTD_LOCALE_BY_LANGUAGE[i18n.language],
    [i18n.language]
  );

  if (isCheckingAuth) {
    return (
      <p data-testid="auth-status">{t('loader.checkingAuthentication')}</p>
    );
  }

  return (
    <ConfigProvider locale={locale}>
      <div className="App">
        <Routes isLoggedIn={isLoggedIn} />
      </div>
    </ConfigProvider>
  );
}

export default App;
